// The partner workspace taxonomy. One registry drives both the mobile tab bar
// and the desktop sidebar, so the two shells cannot drift into different
// products.
//
// WHY THIS IS THREE AND NOT FIVE
//
// It used to be Home, Clients, Work, Inbox, More — five slots, of which three
// (Home, Work, More) were MENUS: screens whose whole content was a list of
// links to somewhere else. That shape cost more than a wasted slot.
//
//   - A menu has to restate what it links to, so the same information appeared
//     twice under different names. Home said "4 intakes need attention", Work
//     said "Intakes in progress · 4" — both from getPartnerWorkSummary below,
//     both linking to PartnerIntakes. Work then rendered those same two
//     destinations a SECOND time under an "All work" heading: four rows, two
//     targets, one screen.
//   - The Home stack held exactly one screen, so every tap on Home was a
//     cross-tab getParent() jump. Home could never be a place you were, only a
//     springboard to the tabs already sitting in the bar beside it.
//   - Pushing real work down a level meant ten screens got registered in two
//     stacks at once so both menus could reach them, which is why Back behaved
//     differently depending on which tab you entered from.
//
// So every primary slot is now somewhere you can actually be, and the menus are
// gone. Clients is the spine — roster → client → plan → funding. Work is the
// queue itself rather than a table of contents for it. Inbox was already a
// destination and is unchanged.

export type PartnerWorkspaceTabName = 'Clients' | 'Work' | 'Inbox' | 'More';

export interface PartnerWorkspaceTab {
  name: PartnerWorkspaceTabName;
  label: string;
  icon: string;
  sidebarIcon: [string, string];
}

/** The three destinations. Every one of them is a screen with data on it. */
export const PARTNER_WORKSPACE_TABS: readonly PartnerWorkspaceTab[] = [
  { name: 'Clients', label: 'Clients', icon: 'users', sidebarIcon: ['fad', 'users'] },
  { name: 'Work', label: 'Work', icon: 'inbox', sidebarIcon: ['fad', 'inbox'] },
  { name: 'Inbox', label: 'Inbox', icon: 'comments', sidebarIcon: ['fad', 'comments'] },
] as const;

/**
 * Account-level tools. These are real destinations too — they are separated
 * from the three above because they are things you visit occasionally, not the
 * work itself.
 *
 * The two shells present them differently ON PURPOSE, because their constraints
 * differ. A phone tab bar runs out of room, so genuine overflow behind a "More"
 * tab is the right pattern there. A desktop sidebar has a column of vertical
 * space doing nothing, so it lists them inline under a rule and the More tab
 * does not exist. That is the one place the two shells legitimately diverge:
 * the same destinations, reached without a menu screen in between on the shell
 * that has room for them.
 */
export interface PartnerWorkspaceTool {
  key: string;
  label: string;
  description: string;
  icon: string;
  sidebarIcon: [string, string];
  /** The capability key in PARTNER_CAPABILITY_ROUTES this opens. */
  capability: string;
  /** An account feature that must be on for the tool to be offered at all.
   *  The same flag the destination itself is gated on, so the tool never
   *  leads to a screen that turns the partner away. */
  feature?: 'all_needs_planning_enabled';
}

export const PARTNER_WORKSPACE_TOOLS: readonly PartnerWorkspaceTool[] = [
  // Funding is most of what an All Needs Planning partner does, and after the
  // menus went it was reachable only from a client record or the roster's
  // Create sheet — ANP could not find it. It is an account tool here, behind
  // the planner's own gate (account_features.all_needs_planning_enabled, which
  // /anp/me re-checks on the screen), listed first because for the partners
  // who have it, it is the work.
  { key: 'funding', label: 'Funding', description: 'Funding simulations and planners for leads and clients.', icon: 'chart-line', sidebarIcon: ['fad', 'chart-line'], capability: 'fundingSimulations', feature: 'all_needs_planning_enabled' },
  { key: 'documents', label: 'Documents', description: 'Your files and client plan documents.', icon: 'folder-open', sidebarIcon: ['fad', 'folder-open'], capability: 'clientDocuments' },
  { key: 'insights', label: 'Insights', description: 'Population-level signals across clients.', icon: 'waveform-lines', sidebarIcon: ['fad', 'waveform-lines'], capability: 'insights' },
  { key: 'ellie', label: 'Ask Ellie', description: 'Ask questions across the partner account.', icon: 'sparkles', sidebarIcon: ['fad', 'sparkles'], capability: 'accountAssistant' },
  { key: 'team', label: 'Team', description: 'Manage people in your partner account.', icon: 'users-gear', sidebarIcon: ['fad', 'users-gear'], capability: 'team' },
  { key: 'settings', label: 'Settings', description: 'Branding, verification, profile, and account controls.', icon: 'gear', sidebarIcon: ['fad', 'gear'], capability: 'partnerSettings' },
  { key: 'support', label: 'Support', description: 'Message the Eleplan support team.', icon: 'life-ring', sidebarIcon: ['fad', 'life-ring'], capability: 'support' },
] as const;

/** Whether a tool is offered to this account: no feature named, or that feature on. */
export function isPartnerToolVisible(tool: PartnerWorkspaceTool, features: Record<string, unknown> | null | undefined): boolean {
  return !tool.feature || features?.[tool.feature] === true;
}

/** The phone-only overflow tab. Absent from the desktop sidebar by design. */
export const PARTNER_OVERFLOW_TAB: PartnerWorkspaceTab = {
  name: 'More', label: 'More', icon: 'ellipsis', sidebarIcon: ['fas', 'ellipsis'],
};

export interface PartnerCapabilityRoute {
  tab: PartnerWorkspaceTabName;
  screen: string;
}

/**
 * Where every shipped capability lives. This is the regression contract: a
 * screen may move, but removing one requires editing this map deliberately.
 *
 * It is only worth anything if it is TRUE. Five of its thirteen entries used to
 * name a tab the screen could not actually be reached from — fundingSimulations
 * claimed Clients while the only door was a row in the More menu, and
 * fundingPlanner claimed a tab with no entry point at all. The test that
 * guarded it grepped the navigator for `name="<screen>"`, which proves a screen
 * is REGISTERED and says nothing about whether a partner can get to it. Both
 * are fixed: the entries below match the shell, and the test walks entry points.
 */
export const PARTNER_CAPABILITY_ROUTES: Record<string, PartnerCapabilityRoute> = {
  // Clients — the spine. Each of these is reachable by drilling from the one
  // above it, which is what makes the roster a spine and not another menu.
  clients: { tab: 'Clients', screen: 'PartnerCustomers' },
  clientPlans: { tab: 'Clients', screen: 'PartnerProjects' },
  fundingSimulations: { tab: 'Clients', screen: 'ANPSimulations' },
  fundingPlanner: { tab: 'Clients', screen: 'ANPPlanner' },

  // Work — the queue.
  intakes: { tab: 'Work', screen: 'PartnerIntakes' },
  invitations: { tab: 'Work', screen: 'PartnerInvites' },

  // Inbox — every conversation, support included. Support used to be
  // registered in two stacks and opened as two independent instances with
  // separate back stacks depending on whether you came from Settings or from
  // Messages. It is a conversation; it belongs where the conversations are.
  messages: { tab: 'Inbox', screen: 'PartnerMessages' },
  support: { tab: 'Inbox', screen: 'PartnerSupportChat' },

  // Tools.
  clientDocuments: { tab: 'More', screen: 'PartnerDocsRoot' },
  insights: { tab: 'More', screen: 'PartnerInsights' },
  accountAssistant: { tab: 'More', screen: 'PartnerEllie' },
  team: { tab: 'More', screen: 'PartnerMembers' },
  partnerSettings: { tab: 'More', screen: 'PartnerSettings' },
};

const OUTSTANDING_INVITATION_STATUSES = new Set(['sent', 'resent', 'registered']);

export function getPartnerWorkSummary(invites: readonly any[] | null | undefined) {
  let pendingIntakes = 0;
  let outstandingInvitations = 0;

  for (const item of invites || []) {
    if (item?.status === 'pending') pendingIntakes += 1;
    else if (OUTSTANDING_INVITATION_STATUSES.has(item?.status)) outstandingInvitations += 1;
  }

  return {
    pendingIntakes,
    outstandingInvitations,
    totalAttention: pendingIntakes + outstandingInvitations,
  };
}

function customerActivityTime(customer: any): number {
  const raw = customer?.last_activity_at || customer?.updated_at || customer?.created_at;
  const parsed = raw ? new Date(raw).getTime() : 0;
  return Number.isFinite(parsed) ? parsed : 0;
}

export function sortRecentPartnerCustomers(customers: readonly any[] | null | undefined, limit = 3): any[] {
  return [...(customers || [])]
    .sort((a, b) => customerActivityTime(b) - customerActivityTime(a))
    .slice(0, Math.max(0, limit));
}
